import { ReactElement, useEffect, useState } from 'react';
import { useCartUpdateItemReference } from '@client/cart/hooks';
import { PRODUCT_ERROR_TYPE, useTranslateProductErrors } from '@client/cart/hooks/cart/useTranslateProductErrors';
import { useTranslate } from '@client/i18n/hooks';
import { useWishlistUpdateItemReference } from '@client/myAccount/wishlists/hooks/wishlist';
import { Alert, ALERT_DISPLAY_TYPE, ALERT_STYLE_TYPE } from '@lib/components/Alert';
import { STYLE_TYPE } from '@lib/components/Form/common';
import { TextInput } from '@lib/components/Form/TextInput';
import classNames from 'classnames';
import { Tooltip } from 'b2b-components/core/components/Tooltip';
import { Props } from './';
import './Reference.scss';

const ITEM_REFERENCE_MAX_LENGTH = 50;

export const Reference = ({
  product,
  disabledMessage,
  isEditable,
  isProductActive,
  isReadOnly,
  isWishlistPage,
  onBlur,
  onChange,
  className,
  showEmptyReferenceField = true,
}: Props): ReactElement | null => {
  const { sku, cart, errors = [], supplierConfig } = product;
  const { reference, quantity } = cart;
  const t = useTranslate();
  const error = useTranslateProductErrors(errors, quantity);
  const defaultValue = supplierConfig?.partAuxiliaryId || reference;
  const cartUpdateItemReference = useCartUpdateItemReference(sku);
  const wishlistUpdateReference = useWishlistUpdateItemReference(sku);
  const updateItemReference = isWishlistPage ? wishlistUpdateReference : cartUpdateItemReference;
  const showLoader = updateItemReference?.isInitialized;
  const [isAutoSaveSuccess, setIsAutoSaveSuccess] = useState(false);

  const handleBlur = (newReference: string): void => {
    if (reference !== newReference) {
      onBlur?.(sku, reference);
    }
  };

  const handleChange = (newReference: string): void => {
    if (!onChange) {
      return;
    }

    onChange(sku, newReference);
  };

  useEffect(() => {
    if (updateItemReference?.isLoaded) {
      setIsAutoSaveSuccess(true);
      const timeout = setTimeout(() => setIsAutoSaveSuccess(false), 2000);
      return () => clearTimeout(timeout);
    }
  }, [updateItemReference?.isLoaded]);

  if (!showEmptyReferenceField && !defaultValue) {
    return null;
  }

  if (showEmptyReferenceField && isProductActive && !isEditable && supplierConfig) {
    return (
      <div className='CartProductReference-InputBox'>
        <TextInput
          textInputId={`${reference}-${sku}`}
          defaultValue={defaultValue || ''}
          styleType={STYLE_TYPE.underlinedFloatingLabel}
          readOnly
          text={{ label: t('Reference') }}
          componentClassName='CartProductReference-Input CartProductReference-Input--Readonly'
          labelClassName='CartProductReference-Label'
          key={reference}
          data-hj-suppress
        />
      </div>
    );
  }

  if (showEmptyReferenceField && !isEditable && !reference && isProductActive) {
    return null;
  }

  return (
    <div className={classNames('CartProductReference', className)}>
      <Tooltip
        content={disabledMessage}
        isHoverable={!!disabledMessage}
        className='CartProductReference-InputBox'
        triggerClassName='CartProductReference-TooltipTrigger'
      >
        <div className="CartProductReference-Wrapper">
          <div className="CartProductReference-Input">
            <TextInput
              textInputId={`${reference}-${sku}`}
              defaultValue={defaultValue || ''}
              maxLength={ITEM_REFERENCE_MAX_LENGTH}
              onBlur={handleBlur}
              onChange={handleChange}
              styleType={STYLE_TYPE.underlinedFloatingLabel}
              disabled={!isProductActive || !isEditable || !!disabledMessage}
              readOnly={isReadOnly}
              text={{
                label: t('Reference (maximum 50 characters)'),
                autoSave: showLoader ? t('Saving...') : t('Saved'),
              }}
              isAutoSave={showLoader}
              isAutoSaveSuccess={isAutoSaveSuccess}
              componentClassName='CartProductReference-Input'
              labelClassName='CartProductReference-Label'
              data-hj-suppress
            />
          </div>
        </div>
      </Tooltip>

      {error?.type === PRODUCT_ERROR_TYPE.error && (
        <Alert
          displayType={ALERT_DISPLAY_TYPE.text}
          styleType={ALERT_STYLE_TYPE.error}
          className='CartProductReference-Alert'
        >
          {error.label}
        </Alert>
      )}

      {error?.type === PRODUCT_ERROR_TYPE.waning && (
        <Alert
          displayType={ALERT_DISPLAY_TYPE.text}
          styleType={ALERT_STYLE_TYPE.warning}
          className='CartProductReference-Alert'
        >
          {error.label}
        </Alert>
      )}
    </div>
  );
};
